import React from 'react';
import { AcvtHomeGame, AcvtSupportedLanguages } from '../../../../types/acvt';
import { usePreview, useT } from '../../../preview';
import style from './style.module.scss';

type FeaturedGameCard = {
    gameObject: AcvtHomeGame;
    /**
     * Kept for backward compatibility — the active language now comes
     * from `usePreview()`.
     */
    language?: AcvtSupportedLanguages;
    opened: React.Dispatch<
        React.SetStateAction<{
            homePromoCard: boolean;
            homeGameCard: boolean;
            promoCard: boolean;
            gameCard: boolean;
        }>
    >;
    setSingleGame: (promo: AcvtHomeGame) => void;
};

export const AcvtFeaturedGameCard = ({
    gameObject,
    opened,
    setSingleGame,
}: FeaturedGameCard) => {
    const t = useT();
    const { language } = usePreview();
    const lang = language as AcvtSupportedLanguages;

    const openDetail = () => {
        setSingleGame(gameObject);
        opened((prevState) => ({ ...prevState, homeGameCard: true }));
    };

    const buttonLabel = gameObject.buttonTitle?.[lang] || t.games.playButton;

    // Theming chosen by the CMS user (per language). Card background,
    // title and description follow the user's pick (with brand-orange
    // fallbacks). The CTA pill is always the app primary red so the
    // call to action stays consistent with the rest of the BU theme.
    const cardStyle: React.CSSProperties = {
        backgroundColor:
            gameObject.backgroundColor?.[lang] ?? 'var(--Brand-Orange-1)',
    };
    const titleStyle: React.CSSProperties = {
        color: gameObject.titleColor?.[lang] ?? 'var(--Brand-Orange-8)',
    };
    const subtitleStyle: React.CSSProperties = {
        color: gameObject.subtitleColor?.[lang] ?? 'var(--Text-Primary)',
    };
    const ctaStyle: React.CSSProperties = {
        backgroundColor: 'var(--preview-primary)',
        color: 'var(--Neutral-1)',
    };

    return (
        <button
            type="button"
            className={style.card}
            onClick={openDetail}
            style={cardStyle}
        >
            <div className={style.text}>
                <span className={style.title} style={titleStyle}>
                    {gameObject.title?.[lang]}
                </span>
                {gameObject.subTitle?.[lang] && (
                    <span className={style.subtitle} style={subtitleStyle}>
                        {gameObject.subTitle?.[lang]}
                    </span>
                )}
            </div>
            <div className={style.image_mask}>
                <img
                    alt=""
                    className={style.image}
                    src={
                        gameObject.detailImage?.[lang] ||
                        gameObject.image?.[lang] ||
                        ''
                    }
                />
            </div>
            <span className={style.cta} style={ctaStyle} aria-hidden>
                {buttonLabel}
            </span>
        </button>
    );
};
